import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, ArrowRight, CheckCircle, Clock, XCircle } from 'lucide-react';
import { projectRaidService } from '../../services/projectRaid';
import { OwnerSelect } from '../common/OwnerSelect';
import { toast } from 'sonner';

interface Dependency {
  id: string;
  description: string;
  type: 'Internal' | 'External';
  dependsOn: string;
  impact: 'High' | 'Medium' | 'Low';
  owner: string;
  status: 'Active' | 'Resolved' | 'Blocked';
  targetDate?: string;
  notes: string;
}

interface DependenciesSectionProps {
  projectId?: number;
  onChanged?: () => void;
}

function dependencyToRaidBody(d: Dependency) {
  return {
    raid_type: 'dependency' as const,
    title: d.description.slice(0, 120) || 'Dependency',
    description: d.description,
    status: d.status,
    owner: d.owner || null,
    due_date: d.targetDate || null,
    extra_metadata: {
      type: d.type,
      dependsOn: d.dependsOn,
      impact: d.impact,
      notes: d.notes,
    },
  };
}

function mapRaidToDependency(item: { id: number; title: string | null; description: string; status: string | null; owner: string | null; due_date: string | null; extra_metadata: Record<string, unknown> | null }): Dependency {
  const meta = item.extra_metadata || {};
  return {
    id: `D-${String(item.id).padStart(3, '0')}`,
    description: item.description || item.title || '',
    type: (meta.type as 'Internal' | 'External') || 'Internal',
    dependsOn: (meta.dependsOn as string) || '',
    impact: (meta.impact as 'High' | 'Medium' | 'Low') || 'Medium',
    owner: item.owner || '',
    status: (item.status as 'Active' | 'Resolved' | 'Blocked') || 'Active',
    targetDate: item.due_date || undefined,
    notes: (meta.notes as string) || '',
  };
}

export function DependenciesSection({ projectId, onChanged }: DependenciesSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingDependency, setEditingDependency] = useState<Dependency | null>(null);
  const [dependencies, setDependencies] = useState<Dependency[]>([]);

  const loadDependencies = useCallback(() => {
    if (!projectId) {
      setDependencies([]);
      return;
    }
    projectRaidService
      .list(projectId, 'dependency')
      .then((list) => setDependencies(list.map(mapRaidToDependency)))
      .catch(() => {
        toast.error('Failed to load dependencies');
        // Do NOT fall back to demo data on real projects — see RisksSection.
        setDependencies([]);
      });
  }, [projectId]);

  useEffect(() => {
    loadDependencies();
  }, [loadDependencies]);

  const handleAddDependency = () => {
    setEditingDependency(null);
    setShowForm(true);
  };

  const handleEditDependency = (dependency: Dependency) => {
    setEditingDependency(dependency);
    setShowForm(true);
  };

  const handleSaveDependency = async (dependency: Dependency) => {
    if (!projectId) {
      if (editingDependency) {
        setDependencies(dependencies.map((d) => (d.id === dependency.id ? dependency : d)));
      } else {
        setDependencies([
          ...dependencies,
          { ...dependency, id: `D-${String(dependencies.length + 1).padStart(3, '0')}` },
        ]);
      }
      setShowForm(false);
      setEditingDependency(null);
      return;
    }
    try {
      if (editingDependency) {
        const numericId = parseInt(editingDependency.id.replace(/[^0-9]/g, ''), 10);
        await projectRaidService.update(projectId, numericId, dependencyToRaidBody(dependency));
        toast.success('Dependency updated');
      } else {
        await projectRaidService.create(projectId, dependencyToRaidBody(dependency));
        toast.success('Dependency added');
      }
      setShowForm(false);
      setEditingDependency(null);
      loadDependencies();
      onChanged?.();
    } catch {
      toast.error('Failed to save dependency');
    }
  };

  const handleDeleteDependency = async (id: string) => {
    if (!projectId) {
      setDependencies(dependencies.filter((d) => d.id !== id));
      return;
    }
    try {
      const numericId = parseInt(id.replace(/[^0-9]/g, ''), 10);
      await projectRaidService.delete(projectId, numericId);
      loadDependencies();
      onChanged?.();
    } catch {
      toast.error('Failed to delete dependency');
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'Resolved') return 'bg-green-50 text-green-700 border-green-200';
    if (status === 'Blocked') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-blue-50 text-blue-700 border-blue-200';
  };

  const getImpactColor = (impact: string) => {
    if (impact === 'High') return 'bg-red-50 text-red-700 border-red-200';
    if (impact === 'Medium') return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-blue-50 text-blue-700 border-blue-200';
  };

  const getTypeColor = (type: string) => {
    if (type === 'External') return 'bg-purple-50 text-purple-700 border-purple-200';
    return 'bg-blue-50 text-blue-700 border-blue-200';
  };

  const metrics = {
    total: dependencies.length,
    active: dependencies.filter((d) => d.status === 'Active').length,
    resolved: dependencies.filter((d) => d.status === 'Resolved').length,
    blocked: dependencies.filter((d) => d.status === 'Blocked').length,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Total Dependencies</div>
            <ArrowRight className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.total}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Active</div>
            <Clock className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.active}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Resolved</div>
            <CheckCircle className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.resolved}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Blocked</div>
            <XCircle className="w-4 h-4 text-red-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.blocked}</div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-end">
        <button
          onClick={handleAddDependency}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Dependency
        </button>
      </div>

      {/* Dependencies Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">ID</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Description</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Depends On</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Type</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Impact</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Owner</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Status</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Target Date</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dependencies.map((dependency) => (
                <tr key={dependency.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-6 text-sm text-gray-600">{dependency.id}</td>
                  <td className="py-4 px-6">
                    <div className="text-sm text-neutral-950">{dependency.description}</div>
                    {dependency.notes && (
                      <div className="text-xs text-gray-500 mt-1">{dependency.notes}</div>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <div className="text-sm text-gray-600 max-w-xs">{dependency.dependsOn}</div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded text-xs border ${getTypeColor(dependency.type)}`}>
                      {dependency.type}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded text-xs border ${getImpactColor(dependency.impact)}`}>
                      {dependency.impact}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-600">{dependency.owner}</td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded text-xs border ${getStatusColor(dependency.status)}`}>
                      {dependency.status}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-600">
                    {dependency.targetDate || '-'}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditDependency(dependency)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDependency(dependency.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <DependencyForm
          dependency={editingDependency}
          onClose={() => {
            setShowForm(false);
            setEditingDependency(null);
          }}
          onSave={handleSaveDependency}
        />
      )}
    </div>
  );
}

// Dependency Form Component
function DependencyForm({
  dependency,
  onClose,
  onSave,
}: {
  dependency: Dependency | null;
  onClose: () => void;
  onSave: (dependency: Dependency) => void;
}) {
  const [formData, setFormData] = useState<Dependency>(
    dependency || {
      id: '',
      description: '',
      type: 'Internal',
      dependsOn: '',
      impact: 'Medium',
      owner: '',
      status: 'Active',
      notes: '',
    }
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl text-neutral-950">
              {dependency ? 'Edit Dependency' : 'Add Dependency'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm mb-2 text-neutral-950">Description *</label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="What is being worked on?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-2 text-neutral-950">Depends On *</label>
            <textarea
              name="dependsOn"
              value={formData.dependsOn}
              onChange={handleChange}
              rows={2}
              placeholder="What does this depend on?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Internal">Internal</option>
                <option value="External">External</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Impact *</label>
              <select
                name="impact"
                value={formData.impact}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Active">Active</option>
                <option value="Resolved">Resolved</option>
                <option value="Blocked">Blocked</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Owner</label>
              <OwnerSelect
                name="owner"
                value={formData.owner}
                onChange={(v) => setFormData({ ...formData, owner: v })}
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Target Date</label>
              <input
                type="date"
                name="targetDate"
                value={formData.targetDate || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-2 text-neutral-950">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              placeholder="Additional notes"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {dependency ? 'Update' : 'Add'} Dependency
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
